/**
 * TabbedCallouts — Weidner Classic override
 *
 * Replaces the rise-theme tabbed panels with a two-column showcase:
 * left = photo-gallery slideshow with offset gray frame and white gap;
 * right = property title, subhead, body, CTA.
 * Slot name kept as TabbedCallouts so Home page wiring stays unchanged.
 *
 * Copy-column motion: heading + subheading slide in from the right, body
 * slides up, CTA scales from back to front. Driven by useScrollReveal
 * state classes on the section (same pattern as PetPolicy).
 */

import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';

// ============================================================================
// Types & constants
// ============================================================================

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  alt_text?: string | null;
}

const SLIDE_INTERVAL_MS = 5000;

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Amenities & Features' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Slideshow
// ============================================================================

function PhotoSlideshow({ photos }: { photos: GalleryPhoto[] }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const count = photos.length;

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % count);
    }, SLIDE_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [count, paused]);

  useEffect(() => {
    if (current >= count && count > 0) setCurrent(0);
  }, [count, current]);

  if (count === 0) {
    return <div className="tabbed-callouts__slide-fallback" aria-hidden="true" />;
  }

  return (
    <div
      className="tabbed-callouts__slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Property photos"
    >
      {photos.map((photo, i) => {
        const src = photo.image ?? photo.original_url ?? '';
        if (!src) return null;
        return (
          <VibesitesImage
            key={`${src}-${i}`}
            url={src}
            alt={photo.alt_text || photo.title || 'Property photo'}
            sizes="(min-width: 992px) 48vw, 100vw"
            className={`tabbed-callouts__slide${i === current ? ' is-active' : ''}`}
            priority={i === 0}
          />
        );
      })}

      {count > 1 && (
        <>
          <button
            type="button"
            className="tabbed-callouts__arrow tabbed-callouts__arrow--prev"
            onClick={() => setCurrent((prev) => (prev - 1 + count) % count)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="tabbed-callouts__arrow tabbed-callouts__arrow--next"
            onClick={() => setCurrent((prev) => (prev + 1) % count)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function TabbedCallouts() {
  const photos = useCollectionBinding<GalleryPhoto>('tabbedcallouts.slides', {
    collection: 'photo_gallery',
    defaultLimit: 8,
  });

  const cta = useSlotGroup('tabbedcallouts.cta', ctaDef);
  const slides = (photos ?? []).filter((p) => p.image || p.original_url);

  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="tabbed-callouts w-full bg-background"
    >
      <div className="tabbed-callouts__inner">
        {/* ── Media column ── */}
        <div className="tabbed-callouts__media">
          <div className="tabbed-callouts__frame" aria-hidden="true" />

          <div className="tabbed-callouts__stage">
            <EditableCollectionBinding
              bindingPath="tabbedcallouts.slides"
              label="Slideshow Photos"
              className="tabbed-callouts__slider-wrap"
            >
              <PhotoSlideshow photos={slides} />
            </EditableCollectionBinding>
          </div>
        </div>

        {/* ── Copy column ── */}
        <div className="tabbed-callouts__copy">
          <div className="tabbed-callouts__copy-block">
            <span className="tabbed-callouts__rule" aria-hidden="true" />
            <div className="tabbed-callouts__headings">
              <RichTextContent
                path="tabbedcallouts.heading"
                label="Heading"
                defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
                as="h2"
                className="tabbed-callouts__heading"
              />
              <RichTextContent
                path="tabbedcallouts.subheading"
                label="Subheading"
                defaultValue="<p>The best of modern living with luxurious layouts and panoramic views in the heart of downtown.</p>"
                as="p"
                className="tabbed-callouts__subheading"
              />
            </div>
          </div>

          <RichTextContent
            path="tabbedcallouts.body"
            label="Body"
            defaultValue='<p><span data-type="field-token" data-field="property_name"></span> puts you at the epicenter of vibrant downtown living, offering custom studios, one, and two-bedroom apartments, and penthouses with sophisticated architecture and designer finishes.</p>'
            as="div"
            className="tabbed-callouts__body"
          />

          <EditableSlotGroup
            groupPath="tabbedcallouts.cta"
            definition={ctaDef}
            className="tabbed-callouts__cta-wrap"
          >
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className="tabbed-callouts__cta"
            >
              {cta.label || 'View Amenities & Features'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .tabbed-callouts {
          padding: 4.5rem 1.25rem 5.5rem;
          overflow-x: clip;
        }
        @media (min-width: 768px) {
          .tabbed-callouts { padding: 5rem 2rem 6rem; }
        }
        @media (min-width: 1200px) {
          .tabbed-callouts { padding: 5.5rem 3rem 6.5rem; }
        }

        .tabbed-callouts__inner {
          max-width: 1180px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          gap: 3rem;
          align-items: center;
        }
        @media (min-width: 992px) {
          .tabbed-callouts__inner {
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
            gap: 4.5rem;
          }
        }

        /* ── Media column ── */
        .tabbed-callouts__media {
          position: relative;
          min-width: 0;
          /* Offset stage so a thicker gray frame peeks on top/left */
          padding: 2.25rem 0 0.5rem 2.25rem;
        }
        @media (min-width: 768px) {
          .tabbed-callouts__media {
            padding: 2.75rem 0 0.75rem 2.75rem;
          }
        }

        .tabbed-callouts__frame {
          position: absolute;
          top: 0;
          left: 0;
          /* Gray block behind — stage overhangs bottom/right */
          right: 2.75rem;
          bottom: 2rem;
          background: color-mix(in srgb, var(--vibe-color-surface, #e8e8e8) 88%, #cfcfcf);
          z-index: 0;
        }

        .tabbed-callouts__stage {
          position: relative;
          z-index: 1;
          aspect-ratio: 4 / 3;
          /* aspect-ratio turns min-height into a min-width; keep it off on phones */
          min-height: 0;
          overflow: hidden;
          background: var(--vibe-color-surface, #e5e5e5);
          /* White ring between gray frame and slideshow — no drop shadow */
          box-shadow: 0 0 0 18px var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .tabbed-callouts__stage {
            aspect-ratio: 5 / 4;
            min-height: 340px;
            box-shadow: 0 0 0 22px var(--vibe-color-background, #fff);
          }
        }
        @media (min-width: 1200px) {
          .tabbed-callouts__stage {
            min-height: 400px;
          }
        }

        .tabbed-callouts__slider-wrap {
          display: block;
          width: 100%;
          height: 100%;
        }
        .tabbed-callouts__slider {
          position: relative;
          width: 100%;
          height: 100%;
          min-height: inherit;
        }
        .tabbed-callouts__slide-fallback {
          width: 100%;
          height: 100%;
          min-height: 260px;
          background: var(--vibe-color-surface, #e5e5e5);
        }
        .tabbed-callouts__slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .tabbed-callouts__slide.is-active {
          opacity: 1;
        }

        .tabbed-callouts__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 3;
          width: 34px;
          height: 34px;
          border: none;
          border-radius: 0;
          background: rgba(0, 0, 0, 0.4);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.25s ease, background 0.25s ease;
        }
        .tabbed-callouts__slider:hover .tabbed-callouts__arrow,
        .tabbed-callouts__slider:focus-within .tabbed-callouts__arrow {
          opacity: 1;
        }
        .tabbed-callouts__arrow:hover {
          background: rgba(0, 0, 0, 0.6);
        }
        .tabbed-callouts__arrow--prev { left: 0.75rem; }
        .tabbed-callouts__arrow--next { right: 0.75rem; }

        /* ── Copy column ── */
        .tabbed-callouts__copy {
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-width: 0;
          overflow-wrap: break-word;
        }

        .tabbed-callouts__copy-block {
          display: flex;
          align-items: stretch;
          gap: 1rem;
        }
        .tabbed-callouts__rule {
          width: 4px;
          flex-shrink: 0;
          background: color-mix(in srgb, var(--vibe-color-text) 55%, #6a6a6a);
          align-self: stretch;
        }
        .tabbed-callouts__headings {
          min-width: 0;
        }

        .tabbed-callouts__heading {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: clamp(2.75rem, 5.5vw, 4.5rem);
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.02;
          text-transform: uppercase;
          color: color-mix(in srgb, var(--vibe-color-text) 62%, #8a8a8a);
        }
        .tabbed-callouts__heading p { margin: 0; }

        .tabbed-callouts__subheading {
          margin: 0.65rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: clamp(0.95rem, 1.35vw, 1.15rem);
          font-weight: 700;
          letter-spacing: 0.01em;
          line-height: 1.35;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        .tabbed-callouts__subheading,
        .tabbed-callouts__subheading p,
        .tabbed-callouts__subheading span {
          color: var(--vibe-color-primary);
        }
        .tabbed-callouts__subheading p { margin: 0; }

        .tabbed-callouts__body {
          margin: 2rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
          max-width: 36rem;
        }
        .tabbed-callouts__body p { margin: 0; }

        .tabbed-callouts__cta-wrap {
          display: block;
          width: fit-content;
          margin-top: 2rem;
          transform-origin: center center;
        }

        .tabbed-callouts__cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-top: 0;
          padding: 0.95rem 1.75rem;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
          width: fit-content;
        }
        .tabbed-callouts__cta:hover {
          background: #b6b6b6;
          color: #000;
        }

        /* Copy-column reveals. useScrollReveal toggles reveal--hidden /
           reveal--visible on the section; children start at rest so
           SSG/SSR never flash off-screen. Heading + subheading slide
           in from the right, body slides up, CTA scales from back. */
        .tabbed-callouts__copy-block,
        .tabbed-callouts__body,
        .tabbed-callouts__cta-wrap {
          transition: opacity 1.2s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 1.2s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .tabbed-callouts.reveal--visible .tabbed-callouts__body {
          transition-delay: 0.2s;
        }
        .tabbed-callouts.reveal--visible .tabbed-callouts__cta-wrap {
          transition-delay: 0.45s;
        }
        .tabbed-callouts.reveal--hidden .tabbed-callouts__copy-block {
          opacity: 0;
          transform: translateX(36px);
        }
        .tabbed-callouts.reveal--hidden .tabbed-callouts__body {
          opacity: 0;
          transform: translateY(36px);
        }
        .tabbed-callouts.reveal--hidden .tabbed-callouts__cta-wrap {
          opacity: 0;
          transform: scale(0.6);
        }
        .tabbed-callouts.reveal--visible .tabbed-callouts__copy-block,
        .tabbed-callouts.reveal--visible .tabbed-callouts__body,
        .tabbed-callouts.reveal--visible .tabbed-callouts__cta-wrap {
          opacity: 1;
          transform: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .tabbed-callouts__slide,
          .tabbed-callouts__cta,
          .tabbed-callouts__copy-block,
          .tabbed-callouts__body,
          .tabbed-callouts__cta-wrap,
          .tabbed-callouts.reveal--hidden .tabbed-callouts__copy-block,
          .tabbed-callouts.reveal--hidden .tabbed-callouts__body,
          .tabbed-callouts.reveal--hidden .tabbed-callouts__cta-wrap {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }

        @media (max-width: 991px) {
          .tabbed-callouts__copy {
            padding-left: 0.25rem;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'TabbedCallouts',
  displayName: 'Tabbed Callouts',
  tokens: {
    'tabbedcallouts.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'tabbedcallouts.subheading': {
      label: 'Subheading',
      type: 'richtext',
      defaultValue:
        '<p>The best of modern living with luxurious layouts and panoramic views in the heart of downtown.</p>',
    },
    'tabbedcallouts.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span> puts you at the epicenter of vibrant downtown living, offering custom studios, one, and two-bedroom apartments, and penthouses with sophisticated architecture and designer finishes.</p>',
    },
  },
  slotGroups: {
    'tabbedcallouts.cta': ctaDef,
  },
  collectionBindings: {
    'tabbedcallouts.slides': {
      label: 'Slideshow Photos',
      collection: 'photo_gallery',
      defaultLimit: 8,
      description: 'Photos shown in the left-column slideshow.',
    },
  },
};

(TabbedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TabbedCallouts;
